// errorId — the short, copyable code an error box shows ("7F3K-29"), pure.
//
// The code names the KIND of failure, not one occurrence: the same failure
// always gets the same code, so a person can read it to support and support
// can map it back (`errorIdOf` over the known subjects). It never shows a
// wire discriminant or other developer text. Occurrence-level correlation
// would need a server request id, which the wire does not carry yet.

import { exhaustSwitchCase } from "@bilda/utils";
import type { ExternalError } from "./externalError.js";

// Failures that start in the client itself rather than at a boundary.
export type LocalFailure =
    | "view-crashed"
    | "workspace-load-failed"
    | "members-load-failed";

type ErrorSubject =
    | { readonly type: "external"; readonly error: ExternalError }
    | { readonly type: "local"; readonly failure: LocalFailure };

const keyOf = (s: ErrorSubject): string => {
    switch (s.type) {
        case "external":
            return s.error._tag === "External.Refused"
                ? `${s.error._tag}:${s.error.errorType}`
                : s.error._tag;
        case "local":
            return `local:${s.failure}`;
        default:
            return exhaustSwitchCase(s);
    }
};

// FNV-1a, 32-bit: small, stable, well spread for short keys.
const fnv1a = (key: string): number =>
    [...key].reduce(
        (h, ch) => Math.imul(h ^ (ch.codePointAt(0) ?? 0), 0x01000193) >>> 0,
        0x811c9dc5,
    );

// Crockford base32: no I, L, O or U, so a code reads aloud unambiguously.
const ALPHABET = "0123456789ABCDEFGHJKMNPQRSTVWXYZ";

// Six symbols (30 bits) as "XXXX-XX".
const errorIdOf = (s: ErrorSubject): string => {
    const h = fnv1a(keyOf(s));
    const symbols = [25, 20, 15, 10, 5, 0].map(
        (shift) => ALPHABET[(h >>> shift) & 31],
    );
    return `${symbols.slice(0, 4).join("")}-${symbols.slice(4).join("")}`;
};

export const externalErrorId = (error: ExternalError): string =>
    errorIdOf({ type: "external", error });

export const localErrorId = (failure: LocalFailure): string =>
    errorIdOf({ type: "local", failure });
